import { useCallback, useRef, useState } from 'react';

/** Opens a menu while the pointer is over the trigger or the menu, and closes it after a selection. */
export function useHoverMenu(closeDelay = 180) {
    const [isOpen, setOpenState] = useState(false);
    const closeTimer = useRef<ReturnType<typeof setTimeout>>();
    const unlockTimer = useRef<ReturnType<typeof setTimeout>>();
    const locked = useRef(false);

    const open = useCallback(() => {
        if (locked.current) return;
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpenState(true);
    }, []);

    const closeSoon = useCallback(() => {
        if (locked.current) return;
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpenState(false), closeDelay);
    }, [closeDelay]);

    const close = useCallback(() => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        locked.current = true;
        setOpenState(false);
        if (unlockTimer.current) clearTimeout(unlockTimer.current);
        // Ignore the hover that is still on the trigger or the menu until the pointer has moved on.
        unlockTimer.current = setTimeout(() => {
            locked.current = false;
        }, 500);
    }, []);

    const setIsOpen = useCallback((next: boolean) => {
        if (next) {
            open();
            return;
        }
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpenState(false);
    }, [open]);

    return { isOpen, setIsOpen, open, closeSoon, close };
}
